// EnvDot.jsx — v200.29 · مؤشّر البيئة داخل الترويسة (2026-02-09)
//
// حلَّ محلّ شريط EnvBanner الكامل، بعد قرار المالك 2026-02-09:
//   · الإنتاج    = نقطة خضراء صغيرة هادئة بلا نصّ.
//   · أي بيئة أخرى = لون تحذيري + اسم البيئة نصّاً ظاهراً دائماً.
// (د٣ · قطبية إلزامية لا تُعكَس. مُثبَت بحارس pytest في
//  tests/test_env_indicator_polarity.py — كسر متعمَّد يُلتقط.)
//
// اللوحة السبع الرسمية فقط:
//   Gold #AA9566 · Deep #3D2A1C · Cream #F7F2EC · White #FFFFFF ·
//   Data-Green #0F766E · Data-Red #B91C1C · Data-Amber #B45309.

import React, { useEffect, useState } from "react";
import { api } from "./lib/api";
import { InfoTip } from "./components/InfoTip";

const OK   = "#0F766E";  // Data-Green — production only
const WARN = "#B45309";  // Data-Amber — non-production, always visible
const DEEP = "#3D2A1C";
const CREAM = "#F7F2EC";

export default function EnvDot({ compact = false } = {}) {
  const [env, setEnv] = useState(null);
  const [matches, setMatches] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const hasToken = () =>
      !!(sessionStorage.getItem("tw_token") || localStorage.getItem("tw_token"));
    const load = async () => {
      try {
        const r1 = await api.get("/env");
        if (cancelled) return;
        setEnv(r1.data);
        if (hasToken()) {
          try {
            const r2 = await api.get("/env/services");
            if (cancelled) return;
            const svs = r2.data?.services || {};
            const m = Object.values(svs)
              .filter((s) => s.identity_matches_production).length;
            setMatches(m);
          } catch { /* not fully authed */ }
        }
      } catch { /* backend down */ }
    };
    load();
    const t = setInterval(load, 30000);
    const onFocus = () => { load(); };
    window.addEventListener("focus", onFocus);
    return () => { cancelled = true; clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, []);

  if (!env) return null;

  const isProd = !!env.is_production;
  const envName = env.name || (isProd ? "Production" : "Preview");
  const isDanger = !isProd && matches > 0;

  // ── قطبية إلزامية (د٣) ──
  // الإنتاج = نقطة صامتة. غير الإنتاج = بارز مع الاسم كاملاً.
  if (isProd) {
    return (
      <span
        data-testid="env-dot-production"
 
        style={{
          display: "inline-block",
          width: 10, height: 10, borderRadius: "50%",
          background: OK, flexShrink: 0,
        }}
      ><InfoTip text="Production" /></span>
    );
  }

  // بيئة غير إنتاجية — الاسم ظاهر دائماً، حتى بدون danger.
  const bg = isDanger ? "rgba(185, 28, 28, 0.10)" : "rgba(180, 83, 9, 0.14)";
  const border = isDanger ? "#B91C1C" : WARN;
  const fg = isDanger ? "#B91C1C" : WARN;

  const gotoDiagnostics = () => {
    window.dispatchEvent(new CustomEvent("tw-navigate",
      { detail: { tab: "system_diagnostics" } }));
  };

  return (
    <button
      type="button"
      data-testid={isDanger ? "env-dot-danger" : "env-dot-nonprod"}
      onClick={isDanger ? gotoDiagnostics : undefined}
 
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: compact ? "3px 8px" : "4px 10px",
        borderRadius: 999,
        background: bg,
        border: `1px solid ${border}`,
        color: fg,
        fontFamily: "'El Messiri', system-ui, sans-serif",
        fontSize: 12, fontWeight: 800,
        cursor: isDanger ? "pointer" : "default",
        flexShrink: 0,
      }}
     aria-label={isDanger ? "بيئة معاينة بمفاتيح إنتاج — افتح التشخيصات" : `بيئة ${envName}`}>
      <span aria-hidden style={{
        width: 8, height: 8, borderRadius: "50%",
        background: fg, display: "inline-block",
      }} />
      <span data-testid="env-dot-name">{envName}</span>
      {isDanger && <span data-testid="env-dot-danger-badge">⚠ {matches}</span>}
    </button>
  );
}
